import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import {
  FiBox, FiUser, FiBriefcase,
  FiEye, FiEyeOff, FiAlertCircle,
  FiMail, FiLock,
} from 'react-icons/fi';
import { Page, LayoutComposer, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import FactoryRegistration from '../../pages/registration/FactoryRegistration';
import AgentRegistration from '../../pages/registration/AgentRegistration';
import BuyerRegistration from '../../pages/registration/BuyerRegistration';
import { signup } from '../../ducks/auth.duck';
import css from './FactoryRegisterPage.module.css';

/** Map known Sharetribe API error codes to human-friendly messages */
const getSignupError = error => {
  if (!error) return null;
  const code = error?.apiErrors?.[0]?.code;
  const apiTitle = error?.apiErrors?.[0]?.title;
  if (code === 'email-taken') return 'This email is already registered. Try logging in instead.';
  if (code === 'email-invalid') return 'The email address you entered is not valid.';
  if (code === 'password-too-short') return 'Password is too short — minimum 8 characters.';
  return apiTitle || error?.message || 'Registration failed. Please try again.';
};

// ─── User Type Selection ───────────────────────────────────────────────────

const USER_TYPES = [
  {
    id: 'factory',
    icon: <FiBox />,
    label: 'Factory',
    description: 'Register your manufacturing facility and start receiving orders.',
  },
  {
    id: 'agent',
    icon: <FiBriefcase />,
    label: 'Agent',
    description: 'Connect buyers with verified Chinese factories and earn commissions.',
  },
  {
    id: 'customer',
    icon: <FiUser />,
    label: 'Buyer / Customer',
    description: 'Source products directly from China with full transparency.',
  },
];

/** Badge label per user type */
const BADGE_LABELS = {
  factory: 'Factory Account',
  agent: 'Agent Account',
  customer: 'Customer Account',
};

// ─── Simple signup form for Agent / Customer ──────────────────────────────

const SimpleSignupForm = ({ userType, onBack }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { signupInProgress, signupError } = useSelector(state => state.auth);

  const validate = values => {
    const errors = {};
    if (!values.firstName?.trim()) errors.firstName = 'First name is required';
    if (!values.lastName?.trim()) errors.lastName = 'Last name is required';
    if (!values.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Enter a valid email address';
    }
    if (!values.password) {
      errors.password = 'Password is required';
    } else if (values.password.length < 8) {
      errors.password = 'Minimum 8 characters';
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  const onSubmit = values => {
    dispatch(
      signup({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        // Pass the exact id string: 'agent' | 'customer'
        publicData: { userType },
      })
    )
      .then(() => history.push('/'))
      .catch(() => {});
  };

  return (
    <div className={css.simpleWrapper}>
      <div className={css.simpleCard}>

        <div className={css.simpleCardTop}>
          <button type="button" className={css.backBtn} onClick={onBack}>
            ← Back
          </button>
        </div>

        <div className={css.simpleHeader}>
          <span className={css.badge}>{BADGE_LABELS[userType]}</span>
          <h2>Create Your Account</h2>
          <p>Fill in the details below to get started on ChinaDolphins.</p>
        </div>

        <Form
          onSubmit={onSubmit}
          validate={validate}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} className={css.simpleForm} autoComplete="off">

              {signupError && (
                <div className={css.apiError}>
                  <FiAlertCircle className={css.alertIcon} />
                  <span>{getSignupError(signupError)}</span>
                </div>
              )}

              {/* First + Last name — side by side to save vertical space */}
              <div className={css.nameRow}>
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    First Name <span className={css.req}>*</span>
                  </label>
                  <Field name="firstName">
                    {({ input, meta }) => (
                      <>
                        <input
                          {...input}
                          autoComplete="given-name"
                          className={`${css.input} ${meta.touched && meta.error ? css.inputError : ''}`}
                          placeholder="First name"
                        />
                        {meta.touched && meta.error && (
                          <span className={css.err}>{meta.error}</span>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    Last Name <span className={css.req}>*</span>
                  </label>
                  <Field name="lastName">
                    {({ input, meta }) => (
                      <>
                        <input
                          {...input}
                          autoComplete="family-name"
                          className={`${css.input} ${meta.touched && meta.error ? css.inputError : ''}`}
                          placeholder="Last name"
                        />
                        {meta.touched && meta.error && (
                          <span className={css.err}>{meta.error}</span>
                        )}
                      </>
                    )}
                  </Field>
                </div>
              </div>

              {/* Email */}
              <div className={css.field}>
                <label className={css.fieldLabel}>
                  Email Address <span className={css.req}>*</span>
                </label>
                <Field name="email">
                  {({ input, meta }) => (
                    <>
                      <div className={`${css.iconInput} ${meta.touched && meta.error ? css.iconInputError : ''}`}>
                        <FiMail className={css.inputIcon} />
                        <input
                          {...input}
                          type="email"
                          autoComplete="email"
                          className={css.inputWithIcon}
                          placeholder="you@example.com"
                        />
                      </div>
                      {meta.touched && meta.error && (
                        <span className={css.err}>{meta.error}</span>
                      )}
                    </>
                  )}
                </Field>
              </div>

              {/* Password */}
              <div className={css.field}>
                <label className={css.fieldLabel}>
                  Password <span className={css.req}>*</span>
                </label>
                <Field name="password">
                  {({ input, meta }) => (
                    <>
                      <div className={`${css.iconInput} ${meta.touched && meta.error ? css.iconInputError : ''}`}>
                        <FiLock className={css.inputIcon} />
                        <input
                          {...input}
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          className={css.inputWithIcon}
                          placeholder="Min. 8 characters"
                          style={{ paddingRight: '44px' }}
                        />
                        <button
                          type="button"
                          className={css.eyeBtn}
                          onClick={() => setShowPassword(v => !v)}
                          tabIndex={-1}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                      {meta.touched && meta.error && (
                        <span className={css.err}>{meta.error}</span>
                      )}
                    </>
                  )}
                </Field>
              </div>

              {/* Confirm Password */}
              <div className={css.field}>
                <label className={css.fieldLabel}>
                  Confirm Password <span className={css.req}>*</span>
                </label>
                <Field name="confirmPassword">
                  {({ input, meta }) => (
                    <>
                      <div className={`${css.iconInput} ${meta.touched && meta.error ? css.iconInputError : ''}`}>
                        <FiLock className={css.inputIcon} />
                        <input
                          {...input}
                          type={showConfirm ? 'text' : 'password'}
                          autoComplete="new-password"
                          className={css.inputWithIcon}
                          placeholder="Re-enter password"
                          style={{ paddingRight: '44px' }}
                        />
                        <button
                          type="button"
                          className={css.eyeBtn}
                          onClick={() => setShowConfirm(v => !v)}
                          tabIndex={-1}
                        >
                          {showConfirm ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                      {meta.touched && meta.error && (
                        <span className={css.err}>{meta.error}</span>
                      )}
                    </>
                  )}
                </Field>
              </div>

              <button type="submit" className={css.submitBtn} disabled={signupInProgress}>
                {signupInProgress ? 'Creating account…' : 'Create Account →'}
              </button>

              <p className={css.loginLink}>
                Already have an account?{' '}
                <NamedLink name="LoginPage">Log in</NamedLink>
              </p>
            </form>
          )}
        />
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────

const layoutAreas = `
  topbar
  main
  footer
`;

const FactoryRegisterPage = () => {
  const [selectedType, setSelectedType] = useState(null);

  const renderContent = () => {
    if (selectedType === 'factory') {
      return (
        <div className={css.factoryWrapper}>
          <div className={css.factorySubbar}>
            <button type="button" className={css.backBtn} onClick={() => setSelectedType(null)}>
              ← Back
            </button>
            <span className={css.factoryBadge}>Factory Registration</span>
          </div>
          <FactoryRegistration />
        </div>
      );
    }

    if (selectedType === 'agent') {
      return (
        <div className={css.factoryWrapper}>
          <div className={css.factorySubbar}>
            <button type="button" className={css.backBtn} onClick={() => setSelectedType(null)}>
              ← Back
            </button>
            <span className={css.factoryBadge}>Agent Registration</span>
          </div>
          <AgentRegistration />
        </div>
      );
    }

    if (selectedType === 'customer') {
      return (
        <div className={css.factoryWrapper}>
          <div className={css.factorySubbar}>
            <button type="button" className={css.backBtn} onClick={() => setSelectedType(null)}>
              ← Back
            </button>
            <span className={css.factoryBadge}>Buyer Registration</span>
          </div>
          <BuyerRegistration />
        </div>
      );
    }

    return (
      <div className={css.root}>
        <div className={css.container}>
          <div className={css.header}>
            <h1>Create Your Account</h1>
            <p>Choose your account type to get started.</p>
          </div>

          <div className={css.cards}>
            {USER_TYPES.map(type => (
              <button
                key={type.id}
                type="button"
                className={css.typeCard}
                onClick={() => setSelectedType(type.id)}
              >
                <div className={css.typeIcon}>{type.icon}</div>
                <h3>{type.label}</h3>
                <p>{type.description}</p>
                <span className={css.selectBtn}>Get Started →</span>
              </button>
            ))}
          </div>

          <p className={css.loginLine}>
            Already have an account?{' '}
            <NamedLink name="LoginPage">Log in</NamedLink>
          </p>
        </div>
      </div>
    );
  };

  return (
    <Page title="Sign Up | ChinaDolphins" scrollingDisabled={false}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {({ Topbar, Main, Footer }) => (
          <>
            <Topbar as="header" className={css.topbar}>
              <TopbarContainer currentPage="SignupPage" />
            </Topbar>
            <Main as="main" className={css.main}>
              {renderContent()}
            </Main>
            <Footer>
              <FooterContainer />
            </Footer>
          </>
        )}
      </LayoutComposer>
    </Page>
  );
};

export default FactoryRegisterPage;
